import { INote, INoteItems, INoteItem } from "../types/INote";
import { Event, LiveBlogPosting, WithContext } from "schema-dts";
import { StructuredDataFragmentsUniversal, StructuredDataFragmentsSport } from "./structured-data-fragments";
import * as Schema from "./schema";
import { ILiveType } from "../types/ILiveType";
import { IPageData } from "../types/IPageData";

const convertNoteToText = ({ note }: { note: INote }): string => {
  const elements = note.content
    .filter((item) => item.type === "text")
    .reduce((acc, item) => [...acc, ...item.elements], [] as INoteItems[]);

  const items = elements
    .filter((item) => item.type === "paragraph")
    .reduce((acc, item) => [...acc, ...(item?.items ?? [])], [] as INoteItem[]);

  const text = items
    .filter((item) => ["text", "externalLink"].includes(item.type))
    .reduce((acc, item) => acc + item.text, "");

  return text;
};

const createStructuredDataUniversal = async ({
  articleId,
  notes,
}: {
  articleId: IPageData["articleId"];
  notes: INote[];
}): Promise<WithContext<LiveBlogPosting>> => {
  const structuredDataFragmentsUniversal = await StructuredDataFragmentsUniversal.fetchData({ articleId });

  return {
    "@context": "https://schema.org",
    ...Schema.createLiveBlogPosting({
      name: structuredDataFragmentsUniversal.getTitle(),
      headline: structuredDataFragmentsUniversal.getHeadline(),
      description: structuredDataFragmentsUniversal.getDescription(),
      url: structuredDataFragmentsUniversal.getURL(),
      mainEntityOfPage: structuredDataFragmentsUniversal.getURL(),
      coverageStartTime: structuredDataFragmentsUniversal.getCoverageStartTime(),
      coverageEndTime: structuredDataFragmentsUniversal.getCoverageEndTime({ notes }),
      datePublished: structuredDataFragmentsUniversal.getDatePublished(),
      dateModified: structuredDataFragmentsUniversal.getDateModified({ notes }),
      image: Schema.createImageObject(structuredDataFragmentsUniversal.getCoverImage()),
      author: structuredDataFragmentsUniversal.getAuthors().map(Schema.createPerson),
      about: Schema.createEvent({
        name: structuredDataFragmentsUniversal.getTitle(),
        startDate: structuredDataFragmentsUniversal.getCoverageStartTime(),
        endDate: structuredDataFragmentsUniversal.getCoverageEndTime({ notes }),
        location: Schema.createVirtualLocation({
          url: structuredDataFragmentsUniversal.getURL(),
        }),
      }),
      publisher: Schema.createOrganization({
        name: structuredDataFragmentsUniversal.getPortalName(),
        logo: Schema.createImageObject(structuredDataFragmentsUniversal.getPortalLogo()),
      }),
      liveBlogUpdate: notes
        .map((note) =>
          Schema.createBlogPosting({
            datePublished: new Date(note.publicationDate),
            articleBody: convertNoteToText({ note }),
          })
        )
        .filter((note) => note.articleBody),
    }),
  };
};

const createStructuredDataSport = async ({
  articleId,
  notes,
}: {
  articleId: IPageData["articleId"];
  notes: INote[];
}): Promise<WithContext<LiveBlogPosting>> => {
  const structuredDataUniversal = await createStructuredDataUniversal({ articleId, notes });

  try {
    const structuredDataUniversalAbout = structuredDataUniversal.about as Required<Event>;
    const structuredDataFragmentsSport = await StructuredDataFragmentsSport.fetchData({ articleId });
    return {
      ...structuredDataUniversal,
      about: Schema.createSportsEvent({
        name: structuredDataUniversalAbout.name.toString(),
        startDate: new Date(structuredDataUniversalAbout.startDate.toString()),
        endDate: structuredDataUniversalAbout.endDate
          ? new Date(structuredDataUniversalAbout.endDate.toString())
          : undefined,
        location: Schema.createPlace({
          name: structuredDataFragmentsSport.getLivePlace(),
        }),
        homeTeam: Schema.createSportsTeam({
          name: structuredDataFragmentsSport.getHomeTeam(),
        }),
        awayTeam: Schema.createSportsTeam({
          name: structuredDataFragmentsSport.getAwayTeam(),
        }),
      }),
    };
  } catch {
    /**
     * Q: What kind of abomination is that?
     * A: Sometimes SPORT Lives do not have Scoreboard Data. The API just returns 404.
     *    In that case, we need to fallback for Structured Data of UNIVERSAL Live.
     */
    return structuredDataUniversal;
  }
};

export const createStructuredData = async ({
  articleId,
  liveType,
  notes,
}: {
  articleId: IPageData["articleId"];
  liveType: ILiveType;
  notes: INote[];
}): Promise<WithContext<LiveBlogPosting>> => {
  switch (liveType) {
    case "SPORT":
      return createStructuredDataSport({ articleId, notes });
    case "UNIVERSAL":
    default:
      return createStructuredDataUniversal({ articleId, notes });
  }
};
